<template>
  <TableLight class="w-full table-fixed">
    <thead>
      <tr>
        <TableH
          class="w-22"
          :label="t('Reviews.agency.reviews.table.column.date')"
          :sort-params="getSortState(ManageUniversalReviewsSortByEnum.Date)"
          @sort="setSort"
        />
        <TableH
          :label="t('Reviews.agency.reviews.table.column.reviewer')"
          :sort-params="getSortState(ManageUniversalReviewsSortByEnum.Reviewer)"
          @sort="setSort"
        />
        <TableH
          :label="t('Reviews.agency.reviews.table.column.company')"
          :sort-params="getSortState(ManageUniversalReviewsSortByEnum.Company)"
          @sort="setSort"
        />
        <TableH
          class="w-26"
          :label="t('Reviews.agency.reviews.table.column.rating')"
          :sort-params="getSortState(ManageUniversalReviewsSortByEnum.Rating)"
          @sort="setSort"
        />
        <TableH class="w-44" :label="t('Reviews.agency.reviews.table.column.status')" />
        <TableH class="w-[84px]" label="" />
      </tr>
    </thead>
    <tbody>
      <tr
        v-for="review in reviews"
        :key="review.id"
        :data-testid="`agency-reviews-row-${review.id}`"
        class="cursor-pointer hover:bg-surface-elevation-2"
        @click="openDetails(review)"
      >
        <td class="text-left">{{ formatDate(review) }}</td>
        <td class="truncate text-left">{{ review.reviewerName || '---' }}</td>
        <td class="text-left break-words">{{ review.companyName || '---' }}</td>
        <td class="text-left">
          <div class="flex items-center">
            <RatingStars :value="ratingValue(review)" dense />
            <span class="ml-1">{{ ratingValueText(review) }}</span>
          </div>
        </td>
        <td class="text-left">
          <UniversalReviewStatusBadge :review="review" />
        </td>
        <td class="text-left">
          <div class="flex flex-nowrap justify-center gap-x-4">
            <ReviewInteractionStatus subject="agency" :type="reminderType(review)" :tooltip-text="reminderTooltip(review)" />
            <ReviewInteractionStatus subject="agency" :type="replyType(review)" />
          </div>
        </td>
      </tr>
    </tbody>
  </TableLight>
</template>

<script setup lang="ts">
import { RatingStars } from '@ramp106/omrjs-core-ui'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'

import type { UniversalReview } from '@/components/ReviewsAgenciesManagements/Reviews/types'
import UniversalReviewStatusBadge from '@/components/ReviewsAgenciesManagements/Reviews/UniversalReviewStatusBadge.vue'
import ReviewInteractionStatus from '@/components/ReviewsCommon/ReviewInteractionStatus/ReviewInteractionStatus.vue'
import { ReviewInteractionType } from '@/components/ReviewsCommon/ReviewInteractionStatus/types'
import { useFormatTableDate } from '@/components/ReviewsManagement/Reviews/useFormatTableDate'
import { ManageUniversalReviewsSortByEnum, SortDirectionEnum, UniversalReviewStates } from '@/gql/reviews'
import { ReviewsRouteName } from '@/router/types'
import { useSortQuery } from '@/ui/TableLight'

defineProps<{ reviews: UniversalReview[] }>()

const { t } = useI18n()
const formatTableDate = useFormatTableDate()
const router = useRouter()
const route = useRoute()
const { setSort, getSortState } = useSortQuery(ManageUniversalReviewsSortByEnum, SortDirectionEnum)

function openDetails(review: UniversalReview) {
  router.push({
    name: ReviewsRouteName.ReviewsAgencyReviewDetails,
    params: { agencyId: route.params.agencyId as string, reviewId: review.id },
  })
}

function formatDate(review: UniversalReview) {
  return formatTableDate(review.createdAt ?? review.publishedAt)
}

function ratingValue(review: UniversalReview) {
  return typeof review.npsScore === 'number' ? review.npsScore / 2 : undefined
}

function ratingValueText(review: UniversalReview) {
  return typeof review.npsScore === 'number' ? (review.npsScore / 2).toFixed(1) : '---'
}

function reminderType(review: UniversalReview) {
  const hasReminders = (review.reminders?.length ?? 0) > 0
  return hasReminders && review.currentStatus !== UniversalReviewStates.Published
    ? ReviewInteractionType.REMINDER_CHECK
    : ReviewInteractionType.REMINDER
}

function reminderTooltip(review: UniversalReview) {
  return t('Reviews.agency.review.reminderStatusHint', { count: review.reminders?.length ?? 0 })
}

function replyType(review: UniversalReview) {
  return review.reviewReply ? ReviewInteractionType.REPLY_CHECK : ReviewInteractionType.REPLY
}
</script>
